import { useCallback, useLayoutEffect, useState } from 'react'
import { FieldPath, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { getCategories } from '../common/category'
import { Child, getChild } from '../common/child'
import { useErrorHandle } from '../common/error/errorHandler'
import { showLoading, notifyMessage } from '../common/store/slices/application'
import { memberUrl } from '../common/constant/appUrl'
import { OperationId } from '../common/constant/operationLog'
import { yesNo } from '../common/constant/classification'
import { useOperationLog } from '../common/operationLog'
import { tabValue } from '../member/memberService'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'
import { PutChildrenDetailInputDto } from '../../dataAccess/webApi/dto/childrenDto'
import { executePutChildrenDetail } from '../../dataAccess/webApi/dao/childrenDao'
import { undefinedPropsToNull } from '../../utils/objectUtil'
import { translate } from '../../i18n'

/** カテゴリーキー */
const CATEGORY_KEYS = ['birthHeight', 'birthWeight']

/** 市民用事業ID（市民用の項目は事業設ID'0'） */
const DEFAULT_PROJECT_ID = '0'
/** 市民用施設ID（市民用のデータは施設ID'0'で作成する） */
const DEFAULT_FACILITY_ID = '0'

/** URLパラメータ */
interface UrlParams {
  childId: string
}

/** かかりつけ医の入力フォームの項目の定義 */
export interface Inputs {
  birthHeightIntPart: number | null
  birthHeightFracPart: number | null
  birthWeight: number | null
}

export type MemberModifyChildBirthInputKey = FieldPath<Inputs>

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const history = useHistory()
  const dispatch = useDispatch()

  // URLパラメータ取得
  const { childId } = useParams<UrlParams>()
  // カテゴリーマスタ
  const [categoryMst, setCategoryMst] = useState<GetCategoryMstDto[]>()
  // お子さま詳細更新日時
  const [updateDatetime, setUpdateDatetime] = useState<string>()
  // // リザルトコード
  const [childrenResultCode, setChildrenResultCode] = useState<number>()
  // オペレーションログ
  const { addOperationLog } = useOperationLog()

  // 入力フォーム初期値設定
  const formMethods = useForm<Inputs>({
    defaultValues: {},
  })

  // 初期処理（ちらつき防止のためにuseLayoutEffect使用）
  useLayoutEffect(() => {
    addOperationLog({
      operationId: OperationId.OP_00000001,
      accessData: [{ userIdRegFlag: yesNo.yes, childId }],
    })

    dispatch(
      showLoading(
        errorHandle(async () => {
          const [category, child] = await Promise.all([
            // カテゴリーマスタ取得
            getCategories({ projectId: DEFAULT_PROJECT_ID, categories: CATEGORY_KEYS }),
            // お子さま情報取得
            getChild(childId, DEFAULT_FACILITY_ID),
          ])

          // カテゴリーマスタ設定
          setCategoryMst(category)
          // お子さま情報設定
          setUpdateDatetime(child.detailUpdateDatetime ?? '')
          // 入力フォームに設定
          formMethods.reset({
            ...formMethods.getValues(),
            ...getInitialInputs(child),
          })
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 確定処理
  const onSubmit = useCallback(
    (data: Inputs) => {
      addOperationLog({ operationId: OperationId.OP_00000037 })

      dispatch(
        showLoading({
          process: errorHandle(async () => {
            // 登録情報を設定
            const regData = getRegistFamilyHospital(data, updateDatetime)
            // 登録実行
            const resp = await executePutChildrenDetail(childId, regData)
            if (resp.resultCode) {
              // 失敗時
              setChildrenResultCode(resp.resultCode)
            } else {
              // 成功時
              dispatch(notifyMessage(translate('memberModifyChildFamilyHospital.success.completionMessage')))
              history.push(memberUrl.url(), { tabValue: tabValue.child, childId })
            }
          }),
          isHiddenMain: false,
        })
      )
    },
    [addOperationLog, updateDatetime]
  )

  return {
    CATEGORY_KEYS,
    formMethods,
    categoryMst,
    onSubmit,
    childrenResultCode,
  }
}

/**
 * 入力フォームの初期値設定
 */
const getInitialInputs = (child: Child) => {
  const birthHeight = child.birthHeight ?? null
  const birthWeight = child.birthWeight ?? null

  const getParts = (value: number | null): [number | null, number | null] => {
    if (value === null) return [null, null]
    const intPart = Math.floor(value)
    const fracPart = Math.round((value - intPart) * 10) // 小数第1位を整数として取得
    return [intPart, fracPart]
  }

  const [birthHeightIntPart, birthHeightFracPart] = getParts(birthHeight)

  return {
    birthHeightIntPart,
    birthHeightFracPart,
    birthWeight,
  }
}

/**
 * お子さま情報登録APIのINPUT用のかかりつけ医情報を取得
 * @param data 入力値
 * @param updateDatetime 更新前更新日時
 * @returns お子さま情報登録APIのINPUT用のかかりつけ医情報
 */
const getRegistFamilyHospital = (data: Inputs, updateDatetime?: string): PutChildrenDetailInputDto => {
  // 共通情報
  const commonInfo = {
    facilityId: DEFAULT_FACILITY_ID,
    updateDatetime: updateDatetime ?? '',
  }

  // 小数を合成するヘルパー関数
  const toDecimal = (intPart: number | null, fracPart: number | null): number | null => {
    if (intPart === null || isNaN(intPart)) return null
    const decimal = fracPart === null || isNaN(fracPart) ? 0 : fracPart
    return parseFloat(`${intPart}.${decimal}`)
  }

  const birthHeight = toDecimal(data.birthHeightIntPart, data.birthHeightFracPart)
  const birthWeight = Number(data.birthWeight)

  return undefinedPropsToNull({
    ...commonInfo,
    birthHeight,
    birthWeight,
  })
}
